<template>
  <div :class="wrapper">
    <!-- showPrev: {{ showPrev }} prevInd:{{ prevInd }} currentInd:{{ currentInd }} -->
    <button
      v-if="showControls && showPrev"
      class="prev"
      type="button"
      :aria-label="$t('object:gallerySlider.prev')"
      data-ident="swipePrev"
      @click.stop.prevent="swipePrev"
    >
      <i class="icon"><icon-chevron-left-alternative /></i>
    </button>
    <div
      v-if="showControls && !showPrev"
      class="prev invisible"
    >
      &nbsp;
    </div>

    <button
      v-if="showControls && showNext"
      class="next"
      type="button"
      :aria-label="$t('object:gallerySlider.next')"
      data-ident="swipeNext"
      @click.stop.prevent="swipeNext"
    >
      <i class="icon"><icon-chevron-right-alternative /></i>
    </button>
    <div
      v-if="showControls && !showNext"
      class="next invisible"
    >
      &nbsp;
    </div>
    <a
      ref="galleryMainLink"
      class="gallery-main-link"
      :href="arp_linkBuilder(galleryLink)"
      data-ident="gallery-main-link"
      @click.prevent="arp_linkClicker(galleryLink)"
    >
      <div
        class="carousel-custom-container "
        @click.prevent="enablePinch ? $emit('pinch', currentInd) : ''"
        @keypress="''"
      >
        <div class="carousel-custom-wrap gallery-slider-height">
          <div
            v-for="(image) in imagesArrCarousel"
            :key="'carousel-custom-item_' + image.position + '_' + image.src"
            :ref="'ref' + image.position"
            v-touch:swipe.left="swipeNext"
            v-touch:swipe.right="swipePrev"
            class="carousel-custom-item"
            :class="{
              'prev-slider': image.position === 'prevInd',
              'active-slider': image.position === 'currentInd',
              'next-slider': image.position === 'nextInd'
            }"
          >
            <lazy-photo
              :object-number="objectNumber"
              :img-src="image.src"
              :img-type="imageType"
              :img-title="image.description"
              :img-no="image.no"
              :img-cdn="image.cdn"
              :img-blur="false"
              :preview-size="previewSize"
              :loading-lazy-attribute="checkLoadingLazyAttribute(image.position)"
              :fetchpriority="image.position === 'currentInd' ? 'high' : undefined"
            />
            <div
              v-if="enableCounter"
              class="carousel-custom-counter"
            >
              {{ image.no }} / {{ imagesArr.length }}
            </div>
            <button
              v-if="enablePinch"
              class="carousel-custom-pinch"
              type="button"
              @click.prevent="$emit('pinch', currentInd);"
            >
              <i class="icon"><icon-expand /></i>
            </button>
          </div>
        </div>
      </div>
    </a>
  </div>
</template>

<script>
import LazyPhoto from 'components/LazyPhoto.vue';
import AnchorRouterPush from 'mixins/AnchorRouterPush';

/* ICONS */
import IconChevronLeftAlternative from 'ICON:IconChevronLeftAlternative';
import IconChevronRightAlternative from 'ICON:IconChevronRightAlternative';
import IconExpand from 'ICON:IconExpand';

import config from 'config/components/search/gallerySliderCustom';

export default {
  name: 'GallerySliderCustom',
  components: {
    LazyPhoto,
    IconChevronLeftAlternative,
    IconChevronRightAlternative,
    IconExpand,
  },
  mixins: [AnchorRouterPush],
  i18nOptions: {
    namespaces: ['object'],
  },
  props: {
    objectNumber: {
      type: [String, Number],
      required: true,
    },
    imagesArr: {
      type: Array,
      default() {
        return [];
      },
    },
    wrapper: {
      type: String,
      default: 'gallery gallery-object gallery-top',
    },
    galleryLink: {
      type: Object,
      default() {
        return {};
      },
    },
    showControls: {
      type: Boolean,
      default: true,
    },
    imageType: {
      type: String,
      default: 'objectDefault',
    },
    enablePinch: {
      type: Boolean,
      default: false,
    },
    enableCounter: {
      type: Boolean,
      default: false,
    },
    imageCountFull: {
      type: Number,
      default: 1,
    },
    startInd: {
      type: Number,
      default: 0,
    },
    endless: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      defaultInd: 0,
      setInd: 0,
    };
  },
  computed: {
    previewSize() {
      return config.previewSize(this.$route);
    },
    showNext() {
      if (this.endless) { return true; }
      if (this.nextInd > this.currentInd) { return true; }
      return false;
    },
    showPrev() {
      if (this.endless) { return true; }
      if (this.prevInd < this.currentInd) { return true; }
      return false;
    },
    currentInd: {
      set(newInd) {
        if (!this.endless && newInd < 0) { // prevent negative value if gallery is not endless
          this.setInd = 0;
        } else {
          this.setInd = newInd;
        }
      },
      get() {
        if (this.setInd === 0) { return 0; } // necessary because this.setInd || this.startInd doesnt work with 0 value
        if (!this.setInd && this.startInd === 0) { return 0; }
        return (this.setInd || this.startInd) || this.defaultInd;
      },
    },
    imagesArrFull() {
      // the slider only works with image array which has at least 3 items
      if (this.imagesArr.length === 1) {
        const imagesReturn = [];
        imagesReturn[0] = JSON.parse(JSON.stringify(this.imagesArr[0]));
        imagesReturn[1] = JSON.parse(JSON.stringify(this.imagesArr[0]));
        imagesReturn[2] = JSON.parse(JSON.stringify(this.imagesArr[0]));
        return imagesReturn;
      } if (this.imagesArr.length === 2) {
        const imagesReturn = [];
        imagesReturn[0] = JSON.parse(JSON.stringify(this.imagesArr[0]));
        imagesReturn[1] = JSON.parse(JSON.stringify(this.imagesArr[1]));
        imagesReturn[2] = JSON.parse(JSON.stringify(this.imagesArr[0]));
        imagesReturn[3] = JSON.parse(JSON.stringify(this.imagesArr[1]));
        return imagesReturn;
      }
      // more than 2 images -> just return given prop imagesArr
      return this.imagesArr;
    },
    imagesArrCarousel() {
      const images = {};
      images.prevInd = { index: this.prevInd, position: 'prevInd', ...this.imagesArrFull[this.prevInd] };
      images.currentInd = { index: this.currentInd, position: 'currentInd', ...this.imagesArrFull[this.currentInd] };
      images.nextInd = { index: this.nextInd, position: 'nextInd', ...this.imagesArrFull[this.nextInd] };

      return images;
    },
    nextInd() {
      if (this.currentInd === this.imagesArrFull.length - 1) {
        return 0;
      }
      return this.currentInd + 1;
    },
    prevInd() {
      if (this.currentInd === 0) {
        return this.imagesArrFull.length - 1;
      }
      return this.currentInd - 1;
    },
  },
  watch: {
    startInd(value) {
      this.setInd = value;
    },
  },
  methods: {
    swipeNext() {
      if (this.imagesArr.length > 1) {
        new Promise((resolve) => {
          this.$refs.refcurrentInd[0].classList.add('leave-left');
          this.$refs.refnextInd[0].classList.add('enter-right');
          // don't update currentInd until css transition is done (300ms)
          setTimeout(() => {
            resolve();
          }, 300);
        }).then(() => {
          this.currentInd = this.nextInd;
        });
        this.$emit('swipe-next');
      }
    },
    swipePrev() {
      if (this.imagesArr.length > 1) {
        new Promise((resolve) => {
          this.$refs.refcurrentInd[0].classList.add('leave-right');
          this.$refs.refprevInd[0].classList.add('enter-left');
          // don't update currentInd until css transition is done (300ms)
          setTimeout(() => {
            resolve();
          }, 300);
        }).then(() => {
          this.currentInd = this.prevInd;
        });
        this.$emit('swipe-prev');
      }
    },
    checkLoadingLazyAttribute(imagePosition) {
      // use this function to determine if img tag gets native loading="lazy" attribute
      // object main image should be loaded directly -> no native loading="lazy"
      // but non visible images in slider do not need to get loaded directly -> use native loading="lazy"
      // see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/img#attr-loading
      return imagePosition !== 'currentInd' ? 'lazy' : false;
    },
  },
};
</script>

<style lang="scss" scoped>
  .wrapper {
    position: relative;
  }

  .gallery {
    position: relative;

    .gallery-main-link {
      width: 100%;
      height: 100%;
    }

    .carousel-custom-container {
      .carousel-custom-wrap {
        .carousel-custom-item {
          width: 100%;
          transform: translateZ(0);
          backface-visibility: hidden;
          will-change: transform;
          cursor: pointer;

          &.leave-left,
          &.leave-right,
          &.enter-left,
          &.enter-right {
            /* if you change transition time, also update setTimeout time in swipe functions */
            transition: all 0s, transform 0.3s;
          }

          &.leave-left {
            transform: translateX(-101%);
          }

          &.leave-right {
            transform: translateX(101%);
          }

          &.prev-slider {
            position: absolute;
            transform: translateX(-101%);

            &.enter-left,
            &.enter-right {
              transform: translateX(0);
            }
          }

          &.next-slider {
            position: absolute;
            transform: translateX(101%);

            &.enter-left,
            &.enter-right {
              position: absolute;
              transform: translateX(0);
            }
          }

          .carousel-custom-counter,
          .carousel-custom-pinch {
            color: #fff;

            @include font_regular;
            @include text_shadow_strong;

            padding: 0.5em;
            position: absolute;
            bottom: 5px;
            font-size: 1.2em;
          }

          .carousel-custom-counter {
            right: 0;
            padding-right: 1.2em;
          }

          .carousel-custom-pinch {
            left: 0;
            padding-left: 1.2em;
            cursor: pointer;
          }
        }
      }
    }
  }

  .lazy-photo {
    width: 100%;
    height: auto;
    position: relative;
  }

  .counter {
    z-index: 1;
    position: absolute;
    bottom: 1.5em;
    right: 1.5em;
    padding: 0;
    color: #fff;

    @include font_size_l;
    @include font_bold;
    @include text_shadow_strong;

    @media #{$media_xs} {
      @include font_size_m;
    }
  }

  .gallery-map {
    position: relative;

    .prev,
    .next {
      position: absolute;
      left: 0;
      top: 50%;
      padding: 0.5em;
      margin: -1em 0 0 0;
      color: #fff;
      line-height: 1em;
      font-size: $font_size_m;
      background: none;
      border: none;

      @include drop_shadow_strong;

      @media #{$media_xs} {
        padding: 0.25em;
      }
    }

    .next {
      right: 0;
      left: auto;
    }

    .carousel-custom-item.prev-slider,
    .carousel-custom-item.next-slider {
      display: none;
    }
  }

  .img {
    position: relative;
    display: block;

    @media #{$media_xl_min} {
      margin: $margin_content_xs 0 $margin_content_xs $margin_content_xs;

      @include radius($radius_card);
    }

    overflow: hidden;

    img {
      width: 100%;
      height: auto;
    }
  }

  .gallery-object {
    position: relative;
    overflow: hidden;
    width: 100%;

    .prev,
    .next {
      z-index: 100;
      position: absolute;
      left: 0;
      top: 45%;
      padding: 1.25em 0.25em;
      margin: -0.75em 0 0 0;
      color: #fff;
      line-height: 1em;
      font-size: $font_size_xl;
      border: none;
      background: rgba(#000, 0.4);
      /* EDVUE-3459 safari workaround to prevent disappearing slider arrows */
      transform: translate3d(0,0,0);
      -webkit-transform: translate3d(0,0,0);

      &.invisible {
        height: 90%;
        width: 2em;
        top: 0;
        background: transparent;
        cursor: not-allowed;
      }
    }

    .prev {
      left: 0;
    }

    .next {
      right: 0;
      left: auto;
    }

    .counter {
      display: none;
    }

    .options {
      z-index: 1;
      font-size: $font_size_xl;
      line-height: 1em;
      position: absolute;
      right: 0.25em;
      top: 0.25em;
      color: #fff;

      @include drop_shadow_strong;

      button {
        padding: 0.25em;
        margin: 0;
        background: none;
        border: none;
        color: #fff;
      }
    }

    &.gallery-object-page {
      a {
        cursor: default;
      }

      div {
        height: 100%;
      }

      .carousel-custom-container {
        .carousel-custom-wrap {
          .carousel-custom-item {
            // prevent cls when blurry img is replaced
            /deep/ .img-full {
              height: 100%;
              object-fit: cover;
              @media #{$media_xs} {
                background-color: $color_grey_bg_light;
                overflow: hidden;
              }
            }
          }
        }
      }
    }
  }
</style>
